// Espelho puro de supabase/functions/_shared/meeting-link.ts.
//
// Existe uma única escada de precedência para "o link da reunião". Antes deste
// módulo cada superfície tinha a sua, e elas discordavam: a timeline do negócio
// lia só `custom_data.meet_link`, o painel da Agenda lia `calendar_events.
// conference_url` e o `{{meet_link}}` das automações lia uma terceira ordem.
// Reuniões vindas do Google (que gravam `custom_data.conference_url`, chave
// diferente) apareciam em branco em algumas telas e preenchidas em outras.
//
// Se a ordem mudar aqui, mudar lá também — e vice-versa. O par de testes
// (src/lib/calendar/meetingLink.test.ts e o .test.ts do lado Deno) usa a mesma
// tabela de casos justamente para travar a divergência entre os dois arquivos.

const FIRST_HTTP_IN_TEXT = /https?:\/\/[^\s<>"']+/i;

/** Fonte de um link, da mais confiável para a menos. */
export type MeetingLinkSource =
  | "activity_column"
  | "calendar_event"
  | "custom_data"
  | "location"
  | "description"
  | "none";

export interface MeetingLinkActivity {
  /** Coluna canônica `activities.conference_url`. */
  conference_url?: string | null;
  custom_data?: unknown;
  description?: string | null;
  location?: string | null;
}

export interface MeetingLinkCalendarEvent {
  conference_url?: string | null;
}

export interface MeetingLinkResult {
  url: string | null;
  source: MeetingLinkSource;
}

/**
 * Normaliza `activities.custom_data` vindo do Supabase.
 * Aceita objeto JSONB ou string JSON (às vezes duplamente serializada em
 * imports/APIs legadas).
 */
export function parseCustomData(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      let parsed: unknown = JSON.parse(trimmed);
      if (typeof parsed === "string") {
        try {
          parsed = JSON.parse(parsed.trim());
        } catch {
          return {};
        }
      }
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }
  return {};
}

/** Só aceita http(s) — descarta "", "  ", "a definir", "Sala 3" etc. */
function asUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) return null;
  return trimmed;
}

/**
 * Escada de precedência do link da reunião.
 *
 * A coluna `activities.conference_url` vence porque é a única escrita por um
 * UPDATE de campo único — as chaves em `custom_data` são gravadas via
 * read-modify-write do JSONB inteiro por três processos concorrentes, o que
 * perde escrita. `calendar_events` vem logo depois por ser reatualizado pelo
 * poll a cada 5 min; as chaves de `custom_data` são snapshots que envelhecem.
 */
export function pickMeetingLink(
  activity: MeetingLinkActivity | null | undefined,
  calendarEvent?: MeetingLinkCalendarEvent | null,
): MeetingLinkResult {
  const fromColumn = asUrl(activity?.conference_url);
  if (fromColumn) return { url: fromColumn, source: "activity_column" };

  const fromCalendar = asUrl(calendarEvent?.conference_url);
  if (fromCalendar) return { url: fromCalendar, source: "calendar_event" };

  const cd = parseCustomData(activity?.custom_data);
  for (
    const key of [
      "conference_url",
      "meet_link",
      "meeting_url",
      "video_link",
      "hangout_link",
      "location",
    ]
  ) {
    const candidate = asUrl(cd[key]);
    if (candidate) return { url: candidate, source: "custom_data" };
  }

  const fromLocation = asUrl(activity?.location);
  if (fromLocation) return { url: fromLocation, source: "location" };

  const desc = typeof activity?.description === "string" ? activity.description.trim() : "";
  if (desc) {
    const match = desc.match(FIRST_HTTP_IN_TEXT);
    // A descrição costuma vir de texto livre, onde a URL cai colada na
    // pontuação da frase ("...entre por https://meet.google.com/abc-def.").
    if (match) return { url: match[0].replace(/[),.;]+$/g, ""), source: "description" };
  }

  return { url: null, source: "none" };
}

/**
 * `requestId` determinístico para `conferenceData.createRequest`.
 *
 * O Google usa esse campo para deduplicar criação de conferência: repetir o
 * mesmo `requestId` devolve a MESMA sala em vez de abrir outra. Os três
 * geradores do CRM usavam `meet-${Date.now()}`, que anula a proteção — cada
 * retentativa (pg_net, cron de recovery, push manual) abria uma sala nova.
 *
 * Sem dependência de crypto para manter a função pura e síncrona nos dois
 * runtimes; o valor só precisa ser estável e colidir pouco, não ser seguro.
 */
export function conferenceRequestId(seed: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < seed.length; i++) {
    const c = seed.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 + c, 0x85ebca6b) >>> 0;
  }
  const hex = h1.toString(16).padStart(8, "0") + h2.toString(16).padStart(8, "0");
  return `meet-${hex}`;
}

/**
 * Tokens de template que trazem o link da reunião para dentro de um texto.
 *
 * A UI de automações oferece os três; desde `crm-automation-webhook-payload.ts`
 * os três resolvem exatamente o MESMO valor canônico (o `custom_data` do payload
 * é sobrescrito com o link vindo desta escada).
 */
export const MEET_LINK_TEMPLATE_TOKENS = [
  "{{meet_link}}",
  "{{custom_data.meet_link}}",
  "{{conference_url}}",
] as const;

/**
 * Bindings de template do WhatsApp Oficial não usam chaves: guardam o caminho
 * pontuado em `variable_mapping[].value`. Casar só nessa forma (e não em
 * qualquer ocorrência da string) evita falso positivo com uma CHAVE chamada
 * `meet_link` dentro de um `action_config`.
 */
const MEET_LINK_BINDING_VALUE = /"value"\s*:\s*"(?:custom_data\.)?(?:meet_link|conference_url)"/;

/** O texto de template referencia o link da reunião? */
export function templateNeedsMeetingLink(text: string | null | undefined): boolean {
  if (!text) return false;
  return MEET_LINK_TEMPLATE_TOKENS.some((token) => text.includes(token));
}

/**
 * A configuração inteira de uma ação (texto livre + bindings) referencia o link?
 *
 * Aceita o objeto ou o JSON já serializado — quem chama às vezes só tem o blob.
 */
export function configNeedsMeetingLink(config: unknown): boolean {
  if (config == null) return false;
  const blob = typeof config === "string" ? config : JSON.stringify(config);
  if (!blob) return false;
  return templateNeedsMeetingLink(blob) || MEET_LINK_BINDING_VALUE.test(blob);
}
